import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Building2, Factory, Landmark, HeartPulse, Home } from "lucide-react";

const SERVICES = [
  {
    icon: Building2,
    title: "Commercial",
    description: "Toitures plates et en pente pour centres commerciaux, bureaux et édifices commerciaux. Solutions durables et performantes.",
    testId: "service-commercial",
  },
  {
    icon: Factory,
    title: "Industriel",
    description: "Systèmes de toiture robustes pour usines, entrepôts et installations industrielles. Résistance et longévité garanties.",
    testId: "service-industriel",
  },
  {
    icon: Landmark,
    title: "Institutionnel",
    description: "Expertise en toiture pour écoles, universités et bâtiments gouvernementaux. Conformité aux normes les plus strictes.",
    testId: "service-institutionnel",
  },
  {
    icon: HeartPulse,
    title: "Santé",
    description: "Solutions spécialisées pour hôpitaux, CHSLD et cliniques. Interventions planifiées minimisant les perturbations.",
    testId: "service-sante",
  },
  {
    icon: Home,
    title: "Résidentiel",
    description: "Réfection et installation de toitures résidentielles. Bardeaux, membranes et systèmes adaptés à votre demeure.",
    testId: "service-residentiel",
  },
];

export default function Services() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section
      id="secteurs"
      data-testid="services-section"
      className="py-24 md:py-32 bg-[#F8FAFC]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12" ref={ref}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="mb-16"
        >
          <p className="tpl-overline mb-4">Nos secteurs d'expertise</p>
          <h2 className="text-4xl md:text-5xl tracking-tight font-bold text-[#0F172A] max-w-2xl leading-tight">
            Des solutions adaptées à
            <span className="text-[#0077cc]"> chaque secteur</span>
          </h2>
        </motion.div>

        {/* Services grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#E2E8F0]">
          {SERVICES.map((service, i) => {
            const Icon = service.icon;
            return (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.4, delay: 0.1 + i * 0.1 }}
                data-testid={service.testId}
                className="service-card bg-white p-8 md:p-10 border border-[#E2E8F0]/40 cursor-default"
              >
                <div className="w-12 h-12 bg-[#0077cc]/10 flex items-center justify-center mb-6">
                  <Icon className="w-6 h-6 text-[#0077cc]" strokeWidth={2} />
                </div>
                <h3 className="text-2xl font-bold text-[#0F172A] mb-3 tracking-tight">
                  {service.title}
                </h3>
                <p className="text-[#475569] text-base leading-relaxed">
                  {service.description}
                </p>
              </motion.div>
            );
          })}

          {/* CTA card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.4, delay: 0.6 }}
            className="bg-[#0A0F1C] p-8 md:p-10 flex flex-col justify-center"
          >
            <p className="text-[#e62e37] text-sm uppercase tracking-[0.2em] font-bold mb-4">
              Besoin d'un expert?
            </p>
            <h3 className="text-2xl font-bold text-white mb-4 tracking-tight">
              Obtenez votre soumission gratuite
            </h3>
            <p className="text-white/60 text-base mb-6 leading-relaxed">
              Notre équipe est prête à évaluer votre projet et vous fournir une soumission détaillée.
            </p>
            <a
              href="tel:5147176662"
              data-testid="services-phone-cta"
              className="inline-flex items-center gap-2 bg-[#e62e37] text-white font-bold uppercase tracking-wide px-6 py-3 text-sm hover:bg-[#cc222b] transition-colors duration-300 w-fit"
            >
              Appelez-nous
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
